import { EditOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Button, Form, message, Space, Spin, Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { PageContext } from '../../../lib/context';
import mutations from '../../../utils/gql/mutations';
import queries from '../../../utils/gql/queries';
import RatesView from './rates-view';

interface Rate {
  id: number;
  name: string;
  lists: object[];
  rate_type: number;
  created_at: Date;
  key: string;
}

interface CustomRate {
  name: string;
  rate: number;
  minimum: number;
}

const RatesController = () => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Rate Type',
      dataIndex: 'rate_type',
      key: 'rate_type',
    },
    {
      title: 'Action',
      key: 'action',
      dataIndex: 'id',
      render: (id: number) => (
        <Space>
          <Tooltip title="Update Rate">
            <Button type="link" onClick={() => _editRate(id)}>
              <EditOutlined />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rateColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
    },
    {
      title: 'Minimum',
      dataIndex: 'minimum',
      key: 'minimum',
    },
  ];

  const _generateKey = (length: number) => {
    var result = [];
    var characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
      result.push(
        characters.charAt(Math.floor(Math.random() * charactersLength))
      );
    }
    return result.join('');
  };

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [rates, setRates] = useState<Rate[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [customRates, setCustomRates] = useState<CustomRate[]>([
    {
      name: '',
      rate: 0,
      minimum: 0,
    },
  ]);

  const [insertRate] = useMutation(mutations.INSERT_RATE, {
    onCompleted(res) {
      form.resetFields();
      _getRates();
      _closeHandler();
      message.success('Rate is created successfully.');
    },
    onError({ graphQLErrors }) {
      message.error('Name must be unique.');
    },
  });

  const [updateRate] = useMutation(mutations.UPDATE_RATE, {
    onCompleted(rs) {
      form.resetFields();
      _getRates();
      _closeHandler();
      message.success('Rate is updated successfully');
    },
    onError({ graphQLErrors }) {
      message.error(JSON.stringify(graphQLErrors));
    },
  });

  const [getRates, { data, loading: rate_loading }] = useLazyQuery(queries.GET_RATE, {
    fetchPolicy: 'no-cache',
  });

  const _submitHandler = () => {
    form.submit();
  };

  const _loading = () => setLoading(true);
  const __loading = () => setLoading(false);

  const _onFinish = (data: any) => {
    let rates: object[] = [];
    customRates.map((val: any, index: number) => {
      rates.push({
        name: data[`name_${index}`],
        rate: data[`rate_${index}`],
        minimum: data[`minimum_${index}`],
      });
      delete data[`name_${index}`];
      delete data[`rate_${index}`];
      delete data[`minimum_${index}`];
    });

    data.lists = {
      data: rates,
    };

    // check if id exists
    if (data.id) {
      delete data.lists;
      rates = [...rates].map((rate: any) => {
        return {
          ...rate,
          rate_id: data.id,
        };
      });
      updateRate({
        variables: {
          data,
          lists: rates,
          id: data.id,
        },
      });
    } else {
      delete data.id;
      insertRate({
        variables: {
          data,
        },
      });
    }
  };

  const _getRates = () => {
    getRates();
  };

  const _addNewRate = () => {
    setCustomRates([...customRates, { name: '', rate: 0, minimum: 0 }]);
  };

  const _deleteRate = (index_val: number) => {
    const rates = [...customRates];
    const items = rates.filter((val, index) => index !== index_val);
    setCustomRates(items);
  };

  const _editRate = (rate_id: number) => {
    form.resetFields();
    const item = rates.filter((val: Rate) => val.id === rate_id);
    let formValues: any = item[0];
    formValues.lists.map((rate: any, index: number) => {
      formValues[`name_${index}`] = rate.name;
      formValues[`rate_${index}`] = rate.rate;
      formValues[`minimum_${index}`] = rate.minimum;
    });
    form.setFieldsValue({ ...formValues });
    const lists = item[0].lists.map((list: any) => {
      delete list.__typename;
      delete list.id;
      return list;
    });
    setCustomRates(lists);
    _showHandler();
  };

  const _showHandler = (status?: Boolean) => {
    if (status) {
      form.resetFields();
      setCustomRates([
        {
          name: '',
          rate: 0,
          minimum: 0,
        },
      ]);
    }
    setShow(true);
  };
  const _closeHandler = () => setShow(false);

  useEffect(() => {
    if (data) {
      const items = data.rates.map((rate: Rate) => {
        return {
          ...rate,
          key: rate.id.toString(),
          create_at: moment(rate.created_at).format('LL'),
        };
      });
      setRates(items);
    }
  }, [data]);

  useEffect(() => {
    _getRates();
  }, [getRates]);

  const value: object = {
    columns,
    form,
    rates,
    customRates,
    rateColumns,
    show,
    rate_loading,
    _showHandler,
    _closeHandler,
    _submitHandler,
    _onFinish,
    _addNewRate,
    _deleteRate,
    _editRate,
    _generateKey,
  };

  return (
    <PageContext.Provider value={value}>
      <Spin spinning={loading}>
        <RatesView />
      </Spin>
    </PageContext.Provider>
  );
};

export default RatesController;
